import {
  Box,
  Camera,
  Layers2,
  Ruler,
  Sun,
  Sparkles,
  Scan,
  MonitorPlay,
  Crosshair,
  BarChart3,
  Clapperboard,
  FileImage,
  MousePointerClick,
  Orbit,
  Copy,
  TriangleAlert,
  Eye,
  AlignHorizontalDistributeCenter,
  Video,
  SunMedium,
  Blend,
  Cog,
  Pause,
  Projector,
} from 'lucide-react';
import type { FeatureGroup } from '../types';

export const viewerGroup: FeatureGroup = {
  id: 'viewer',
  label: '3D Viewer & Scene',
  headline: 'Professional-grade rendering, in the browser',
  summary:
    "Meshpit's viewer is built on Three.js with a full post-processing pipeline. Every visual option — lights, shadows, ambient occlusion, bloom, custom grids, picture-in-picture cameras, SVG overlays, annotations, and geometry animation — is composable directly from your SCAD code.",
  accentClass: 'from-slate-500 to-zinc-400',
  items: [
    {
      icon: Box,
      title: 'PBR Materials',
      description:
        'Physically-based rendering with metalness, roughness, clearcoat, sheen, iridescence, and transmission. Apply per-object with material_def() and material(), or set scene-wide defaults with scene_material().',
      tier: 'major',
    },
    {
      icon: Sun,
      title: 'Lighting System',
      description:
        'All four Three.js light types — ambient, directional, point, and spot — configurable from SCAD code. Light sources are visualised as helpers in the scene. Built-in three-point rig intensities are tunable with scene_default_lights().',
      tier: 'major',
    },
    {
      icon: SunMedium,
      title: 'Animated Lights — LFOs & Orbits',
      description:
        'Any light can orbit the model and animate its intensity, color, angle, or penumbra with LFO waveforms (sine, triangle, square, sawtooth). An elliptical orbit mode and drift parameters produce complex moving light rigs.',
      tier: 'major',
    },
    {
      icon: Projector,
      title: 'Gobo / Light Projection Maps',
      description:
        'Project any SVG or PNG through a spot light as a gobo — the image masks and patterns the cone just like a theatrical gobo or patterned gel. Pass map="filename.svg" to light_spot(). Works on static, orbiting, and LFO-animated spots.',
      tier: 'minor',
    },
    {
      icon: Sparkles,
      title: 'Post-Processing — SSAO & Bloom',
      description:
        'Screen-space ambient occlusion darkens crevices and contact shadows. Bloom adds a glow around bright and emissive surfaces. Both are fully configurable from code via scene_ssao() and scene_bloom().',
      tier: 'major',
    },
    {
      icon: Camera,
      title: 'Picture-in-Picture Cameras',
      description:
        'Define additional camera viewports directly in your SCAD code. Each PiP can be dragged, resized, given a label, and set to grayscale or wireframe mode — great for technical drawings and exploded views.',
      tier: 'major',
    },
    {
      icon: Clapperboard,
      title: 'Cinematic Camera Transitions',
      description:
        'Clicking a PiP panel animates the main camera with a fully programmable transition. Nine composable effects — orbit, zoom_out, arc, roll, tilt, shake, bounce, fov_pulse, and direct — combine freely with easing and randomness controls.',
      tier: 'major',
    },
    {
      icon: Video,
      title: 'Intro Camera Path',
      description:
        'Define a cinematic fly-in sequence with camera_path() waypoints that plays automatically when the model first loads. Shares all transition effects with PiP cameras. A Replay button appears in the toolbar.',
      tier: 'major',
    },
    {
      icon: FileImage,
      title: 'SVG Overlays',
      description:
        'Pin any SVG from your project as a flat 2-D overlay on the viewer — logos, watermarks, scale bars, draft badges. Position and size from code using fractions or pixels, with optional currentColor theming and per-overlay opacity.',
      tier: 'major',
    },
    {
      icon: Layers2,
      title: 'Multi-View',
      description:
        'Split the viewport into multiple simultaneous views — top, front, side, and perspective — to inspect your model from every angle at once.',
      tier: 'major',
    },
    {
      icon: MonitorPlay,
      title: 'Scene Grids & Backgrounds',
      description:
        'Define custom grids from code with scene_grid() — flat plane grids, cube wireframes (print volume), or sphere wireframes (clearance constraint). Multiple additive calls are supported. Set the viewport background color with scene_background().',
      tier: 'major',
    },
    {
      icon: Ruler,
      title: 'Measurement Tool',
      description:
        'Click any two points on the model to measure the distance between them directly in the 3D scene — no exporting required.',
      tier: 'major',
    },
    {
      icon: Crosshair,
      title: 'Annotations',
      description:
        'Attach floating labels to any point in the 3D scene from code. Set alwaysVisible to keep them shown regardless of camera angle.',
      tier: 'major',
    },
    {
      icon: MousePointerClick,
      title: 'Model Interaction',
      description:
        'Click objects in the 3D scene to toggle, set, or cycle a SCAD parameter. interactive_publish() fires a pub/sub event instantly — no recompile — for texture and shader scripts that react in real time.',
      tier: 'major',
    },
    {
      icon: Orbit,
      title: 'Preview Motion',
      description:
        'Wrap any geometry with motion() to animate it in the preview without recompiling. Four additive layers: axis translation, orbital revolution, continuous spin, and rock — all driven by the same LFO system as lights.',
      tier: 'major',
    },
    {
      icon: Copy,
      title: 'Instant Mesh Cloning',
      description:
        'Tag any compiled geometry with clone_ref() and place unlimited copies with clone(). Each clone is a free Three.js transform — position, rotation, and scale — with zero extra WASM compilation.',
      tier: 'major',
    },
    {
      icon: Eye,
      title: 'Ghost & Transparency',
      description:
        'Mark objects as ghost or transparent in code to see through enclosures, visualise internal geometry, or create exploded assembly views.',
      tier: 'major',
    },
    {
      icon: TriangleAlert,
      title: 'Geometric Constraints',
      description:
        'Add a // @constraint comment before any ghost object to enforce live geometric rules — build-volume containment, keep-out zones, and minimum clearance gaps. Violations surface as an amber warning panel the moment your model breaks the rule.',
      tier: 'major',
    },
    {
      icon: BarChart3,
      title: 'Object Inspector',
      description:
        'Inspect individual objects in the scene — see their bounding box, vertex count, material, and position without leaving the viewer.',
      tier: 'minor',
    },
    {
      icon: Scan,
      title: 'Polygon Mode',
      description:
        'View your model as a flat polygon mesh to inspect the raw geometry output from OpenSCAD before rendering.',
      tier: 'minor',
    },
    {
      icon: Blend,
      title: 'Wireframe & Grayscale Modes',
      description:
        'Toggle wireframe rendering (with a solid-to-wireframe opacity slider) or flatten all colors to grayscale for geometry-focused review.',
      tier: 'minor',
    },
    {
      icon: Blend,
      title: 'Shadows',
      description:
        'Soft VSM shadows or hard shadows, with adjustable opacity and shadow-map resolution. Toggle and tune from code with scene_shadows() or from the Advanced Scene Settings panel.',
      tier: 'minor',
    },
    {
      icon: Cog,
      title: 'scene_camera() — Initial Camera',
      description:
        "Set the camera's starting position, look-at target, field of view, and projection type from code. Applied instantly as you type — before WASM compiles.",
      tier: 'minor',
    },
    {
      icon: AlignHorizontalDistributeCenter,
      title: 'Presentation & Viewer Modes',
      description:
        'Presentation mode auto-rotates the model for a looping display. Toggle the world axis helper, fit the model to the viewport with one key, and show or hide excluded (~) objects independently.',
      tier: 'minor',
    },
    {
      icon: Pause,
      title: 'Animation Pause',
      description:
        'A pause button appears in the toolbar automatically whenever any motion(), light LFO, or light orbit animation is active — freeze the scene at any frame.',
      tier: 'minor',
    },
  ],
};
